import './PitcherSessionTiltChart.scss';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PitcherSessionTiltChartConfigDto, PitcherSessionTiltChartDataDto, PitcherSessionTiltChartDto } from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { useChartDataDisplay } from 'hooks/helperHooks';
import useReportStore from 'hooks/SessionReport/Pitcher/reportStore';
import { pitchTypeGroups, pitchTypes } from 'utilities/constants';
import { pitchTypeGroupToColor, pitchTypeToColor } from 'utilities/colorHelpers';
import { Tooltip } from '@trackman/web-shared-components';

type TiltPlot = {
  x: number;
  y: number;
  data: PitcherSessionTiltChartDto;
};

const PitcherSessionTiltChart = ({
  chartData,
  isLoading,
  extraChartData,
}: ChartProps<PitcherSessionTiltChartDataDto, PitcherSessionTiltChartConfigDto>) => {
  const { selectedConfgValues } = useReportStore();

  const canvasSize = 1000;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tiltPlots, setTiltPlots] = useState<Array<TiltPlot>>([]);

  const simulateChartData = useCallback(
    (isLoading: boolean) => {
      if (!isLoading) {
        return [];
      }

      const simulatedChartData: PitcherSessionTiltChartDto[] = [];

      const pitchTypeLabels = selectedConfgValues.pitchGrouping === 'Types' ? pitchTypes : pitchTypeGroups;
      for (const pitchType of pitchTypeLabels.filter((pitchType) => pitchType !== 'Undefined')) {
        for (let i = 0; i < 10; i++) {
          const hour = Math.floor(Math.random() * 12) + 1;
          const minute = Math.floor(Math.random() * 60);
          simulatedChartData.push({ pitchType, tilt: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}` });
        }
      }

      return simulatedChartData;
    },
    [selectedConfgValues.pitchGrouping]
  );

  const chartDataDisplay = useChartDataDisplay(chartData?.tiltData, simulateChartData, isLoading);

  const dataMap = useMemo(() => {
    const map = new Map<string, PitcherSessionTiltChartDto[]>();
    chartDataDisplay.forEach((item) => {
      const pitchType = item.pitchType;
      if (map.has(pitchType)) {
        map.get(pitchType)?.push(item);
      } else {
        map.set(pitchType, [item]);
      }
    });
    return map;
  }, [chartDataDisplay]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const newTiltPlots = renderTiltChart(canvas, dataMap);
    setTiltPlots(newTiltPlots);
  }, [dataMap]);

  return (
    <div className='PitcherSessionTiltChart-container'>
      <div className='PitcherSessionTiltChart-chart'>
        <canvas ref={canvasRef} width={canvasSize} height={canvasSize}></canvas>
        {tiltPlots.map((tiltPlot, i) => (
          <Tooltip title={tiltPlot.data.tilt} placement='bottom' arrow key={i}>
            <div
              style={{
                position: 'absolute',
                left: `${(tiltPlot.x / canvasSize) * 100}%`,
                top: `${(tiltPlot.y / canvasSize) * 100}%`,
                height: '16px',
                width: '16px',
                transform: 'translate(-50%, -50%)',
              }}
            ></div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default PitcherSessionTiltChart;

const renderTiltChart = (canvas: HTMLCanvasElement, data: Map<string, PitcherSessionTiltChartDto[]>): TiltPlot[] => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return [];
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const textRadius = Math.min(canvas.width, canvas.height) * 0.45;
  const lineRadius = Math.min(canvas.width, canvas.height) * 0.41;
  const chartRadius = Math.min(canvas.width, canvas.height) * 0.4;

  // Draw donuts
  const startRadius = chartRadius * 0.2;

  const ringSize = (chartRadius - startRadius) / data.size;
  const ringSpacing = ringSize * 0.25;
  const desiredDonutWidth = ringSize - ringSpacing;

  const maxDonutWidth = (chartRadius - startRadius) * 0.25;
  const donutWidth = Math.min(desiredDonutWidth, maxDonutWidth);

  for (let i = 0; i < data.size; i++) {
    const innerRadius = startRadius + i * (donutWidth + ringSpacing);
    const outerRadius = innerRadius + donutWidth;

    drawDonut(ctx, centerX, centerY, outerRadius, innerRadius, '#f2f2f2');
  }

  // Draw clock
  const startAngleOffset = -Math.PI / 2;
  const clockStep = (2 * Math.PI) / 12;

  for (let i = 0; i < 12; i++) {
    const angle = i * clockStep + startAngleOffset;

    const endX = centerX + lineRadius * Math.cos(angle);
    const endY = centerY + lineRadius * Math.sin(angle);

    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(endX, endY);
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 2;
    ctx.stroke();

    const number = i === 0 ? 12 : i;
    const textX = centerX + textRadius * Math.cos(angle);
    const textY = centerY + textRadius * Math.sin(angle);

    ctx.font = `bold 40px Inter`;
    ctx.fillStyle = '#7B7B7B';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(number), textX, textY);
  }

  // Draw circles
  const circleRadius = donutWidth * 0.4;
  const tiltPlots: TiltPlot[] = [];

  data.entries().forEach(([pitchType, values], index) => {
    const innerRadius = startRadius + index * (donutWidth + ringSpacing);

    for (const value of values) {
      const [hourStr, minuteStr] = value.tilt.split(':');
      let hour = parseInt(hourStr);
      const minute = parseInt(minuteStr);

      if (hour === 12) {
        hour = 0;
      }

      const totalHours = hour + minute / 60;
      const angle = (totalHours / 12) * (2 * Math.PI);
      const adjustedAngle = angle - Math.PI / 2;
      const circleX = centerX + (innerRadius + donutWidth * 0.5) * Math.cos(adjustedAngle);
      const circleY = centerY + (innerRadius + donutWidth * 0.5) * Math.sin(adjustedAngle);
      const color = pitchTypeToColor[pitchType] ?? pitchTypeGroupToColor[pitchType];

      ctx.beginPath();
      ctx.arc(circleX, circleY, circleRadius, 0, 2 * Math.PI, false);
      ctx.fillStyle = color + '80';
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 4;
      ctx.stroke();

      tiltPlots.push({
        x: circleX,
        y: circleY,
        data: value,
      });
    }
  });

  return tiltPlots;
};

const drawDonut = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  outerRadius: number,
  innerRadius: number,
  color: string
) => {
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius, 0, 2 * Math.PI, false);
  ctx.arc(centerX, centerY, innerRadius, 0, 2 * Math.PI, true);
  ctx.fillStyle = color;
  ctx.fill();
};
